
import React, { useState, useRef, useEffect } from 'react';

interface Props {
  userEmail?: string | null;
  userAvatar?: string | null;
  onLogout?: () => void;
  showInstallButton?: boolean;
  onInstallClick?: () => void;
  isGoogleConnected?: boolean;
}

const Header: React.FC<Props> = ({ userEmail, userAvatar, onLogout, showInstallButton, onInstallClick, isGoogleConnected }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="bg-white/70 backdrop-blur-xl border-b border-indigo-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <img src="/logo.png" alt="MeetingBot Logo" className="w-10 h-10 object-contain" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight lowercase" style={{ letterSpacing: '-0.03em' }}>MeetingBot</h1>
            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.15em]">Intelligence</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          {showInstallButton && (
            <button 
              onClick={onInstallClick}
              className="hidden sm:flex items-center space-x-1 bg-indigo-600 text-white px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider hover:bg-indigo-700 transition-colors shadow-sm"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span>Install App</span>
            </button>
          )}
          <div className="hidden sm:block">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-100 uppercase tracking-wider">
              Intelligence Engine Active
            </span>
          </div>
          {(isGoogleConnected || userEmail) && (
            <div className="relative" ref={dropdownRef}>
              <button 
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center overflow-hidden border-2 border-indigo-200 shadow-sm hover:border-indigo-400 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                {userAvatar ? (
                  <img src={userAvatar} alt="User Avatar" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-indigo-200 text-indigo-600 font-bold text-lg">
                    {userEmail ? userEmail.charAt(0).toUpperCase() : '?'}
                  </div>
                )}
              </button>

              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-100 py-2 z-50">
                  <div className="px-4 py-3 border-b border-slate-50 mb-1">
                    <p className="text-xs font-bold text-slate-900 truncate">{userEmail || 'Connected Account'}</p>
                    <p className="text-[10px] text-slate-500">Connected to Workspace</p>
                  </div>
                  {onLogout && (
                    <button 
                      onClick={() => {
                        setIsDropdownOpen(false);
                        onLogout();
                      }} 
                      className="w-full text-left px-4 py-2 text-xs text-red-600 hover:bg-red-50 font-medium transition-colors flex items-center space-x-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      <span>Disconnect Workspace</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
